import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { TrendingUp, ArrowDown, Filter, LineChart, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  LabelList,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import ComplaintsNegativityChart from './ComplaintsNegativityChart';
import CallTopicsChart from './CallTopicsChart';
import { fetchCallDynamics, fetchUpsellFunnel, getCallClassificationTypes } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import useTimeZone from '../hooks/useTimeZone';
import { formatDayMonthYear } from '../utils/dateTime';
import { formatClassificationType } from '../utils/callClassification';

const defaultRange = {
  from: subDays(new Date(), 29),
  to: new Date()
};

const callsAxisFormatter = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU') : value ?? '—';

const callDynamicsTooltipFormatter = (entry) => {
  if (!entry || typeof entry.value !== 'number') {
    return '—';
  }

  return `${entry.value.toLocaleString('ru-RU')}`;
};

const createValueLabelRenderer = (color, formatter) => (props) => {
  const { x, y, value, width } = props;
  if (value === null || value === undefined) {
    return null;
  }

  const baseX = typeof x === 'number' ? x : 0;
  const centerX = typeof width === 'number' ? baseX + width / 2 : baseX;

  return (
    <text
      x={centerX}
      y={(y ?? 0) - 8}
      textAnchor="middle"
      fill={color}
      fontSize="9"
      fontWeight="600"
    >
      {formatter ? formatter(value) : value}
    </text>
  );
};

const tooltipWrapperStyle = { zIndex: 30 };

const formatCallDynamicsLabel = (point, timeZone) => {
  if (!point) {
    return '—';
  }

  const granularity = point.granularity?.toLowerCase();
  if (granularity === 'hour' || granularity === 'minute') {
    return point.label ?? '—';
  }

  const raw = point.day ?? point.label;
  if (!raw) {
    return '—';
  }

  if (raw instanceof Date || typeof raw === 'string') {
    const formatted = formatDayMonthYear(raw, timeZone);
    return formatted === '—' ? String(raw) : formatted;
  }

  return typeof raw === 'string' ? raw : String(raw);
};

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span>
            {entry.name ?? entry.dataKey}: {formatter ? formatter(entry) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const GeneralStatistics = ({ dateRange, callType = 'all' }) => {
  const [chartType, setChartType] = useState('bar');
  const [callDynamics, setCallDynamics] = useState([]);
  const [classificationTypes, setClassificationTypes] = useState([]);
  const [selectedClassificationType, setSelectedClassificationType] = useState('');
  const [upsellFunnel, setUpsellFunnel] = useState(null);
  const [isLoadingDynamics, setIsLoadingDynamics] = useState(false);
  const [isLoadingFunnel, setIsLoadingFunnel] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingDynamics = useRef(false);
  const isFetchingFunnel = useRef(false);
  const { timeZone } = useTimeZone();

  const activeDateRange = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return dateRange;
    }

    return defaultRange;
  }, [dateRange]);

  const dateRangeLabel = useMemo(() => {
    if (!activeDateRange?.from || !activeDateRange?.to) {
      return 'за выбранный период';
    }

    try {
      const fromLabel = formatDayMonthYear(activeDateRange.from, timeZone);
      const toLabel = formatDayMonthYear(activeDateRange.to, timeZone);
      return `за период ${fromLabel} — ${toLabel}`;
    } catch {
      return 'за выбранный период';
    }
  }, [activeDateRange, timeZone]);

  const loadClassificationTypes = useCallback(async () => {
    try {
      const data = await getCallClassificationTypes();
      setClassificationTypes(Array.isArray(data) ? data : []);
    } catch {
      setClassificationTypes([]);
    }
  }, []);

  const loadCallDynamics = useCallback(async (showSpinner = true) => {
    if (!activeDateRange?.from || !activeDateRange?.to || isFetchingDynamics.current) {
      return;
    }

    if (showSpinner) {
      setIsLoadingDynamics(true);
    }

    isFetchingDynamics.current = true;
    setError(null);
    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_classification_type: selectedClassificationType,
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchCallDynamics(params);
      setCallDynamics(data ?? []);
    } catch {
      setError('Не удалось загрузить динамику звонков.');
    } finally {
      isFetchingDynamics.current = false;
      if (showSpinner) {
        setIsLoadingDynamics(false);
      }
    }
  }, [activeDateRange, callType, selectedClassificationType]);

  const loadFunnel = useCallback(async (showSpinner = true) => {
    if (!activeDateRange?.from || !activeDateRange?.to || isFetchingFunnel.current) {
      return;
    }

    if (showSpinner) {
      setIsLoadingFunnel(true);
    }

    isFetchingFunnel.current = true;
    setError(null);
    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchUpsellFunnel(params);
      setUpsellFunnel(data ?? null);
    } catch {
      setError('Не удалось загрузить воронку допродаж.');
    } finally {
      isFetchingFunnel.current = false;
      if (showSpinner) {
        setIsLoadingFunnel(false);
      }
    }
  }, [activeDateRange, callType]);

  useEffect(() => {
    loadClassificationTypes();
  }, [loadClassificationTypes]);

  useEffect(() => {
    loadCallDynamics();
  }, [loadCallDynamics]);

  useEffect(() => {
    loadFunnel();
  }, [loadFunnel]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadCallDynamics(false);
      loadFunnel(false);
    }, GRAPH_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loadCallDynamics, loadFunnel]);

  const maxCalls = useMemo(() => {
    if (!callDynamics.length) return 1;
    return Math.max(
      ...callDynamics.map((point) =>
        Math.max(point.calls ?? 0, point.incomingCalls ?? 0, point.outgoingCalls ?? 0)
      )
    );
  }, [callDynamics]);

  const dynamicsUnitLabel = useMemo(() => {
    const granularity = callDynamics?.[0]?.granularity;
    if (granularity === 'minute') return 'минуту';
    if (granularity === 'hour') return 'час';
    return 'день';
  }, [callDynamics]);

  const lineChartData = useMemo(
    () =>
      (callDynamics ?? []).map((point) => {
        const totalCalls = point.calls ?? 0;
        const incomingCalls = point.incomingCalls ?? 0;
        const outgoingCalls = point.outgoingCalls ?? 0;
        return {
          label: formatCallDynamicsLabel(point, timeZone),
          calls: totalCalls,
          incomingCalls,
          outgoingCalls
        };
      }),
    [callDynamics, timeZone]
  );

  const funnelSteps = useMemo(() => {
    const orders = upsellFunnel?.totalOrders ?? 0;
    const attempts = upsellFunnel?.upsellAttempts ?? 0;
    const successes = upsellFunnel?.upsellSuccesses ?? 0;

    return [
      {
        key: 'orders',
        title: 'Оформление заказа',
        value: orders,
        ratio: 100,
        color: '#2563eb'
      },
      {
        key: 'attempts',
        title: 'Попытка допродажи',
        value: attempts,
        ratio: upsellFunnel?.attemptRate ?? (orders > 0 ? (attempts * 100) / orders : 0),
        color: '#f59e0b'
      },
      {
        key: 'successes',
        title: 'Успешная допродажа',
        value: successes,
        ratio: upsellFunnel?.successRate ?? (attempts > 0 ? (successes * 100) / attempts : 0),
        color: '#22c55e'
      }
    ];
  }, [upsellFunnel]);

  const toggleChartType = () => {
    setChartType((prev) => (prev === 'bar' ? 'line' : 'bar'));
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Динамика звонков
            </CardTitle>
            <CardDescription>Всего звонков {dateRangeLabel}</CardDescription>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <label className="text-sm text-muted-foreground">
              Тема:
              <select
                value={selectedClassificationType}
                onChange={(event) => setSelectedClassificationType(event.target.value)}
                className="ml-2 rounded-md border bg-background px-2 py-1 text-sm text-foreground"
              >
                <option value="">Все</option>
                {classificationTypes.map((type) => (
                  <option key={type} value={type}>
                    {formatClassificationType(type)}
                  </option>
                ))}
              </select>
            </label>
            <ChartToggleButton chartType={chartType} onToggle={toggleChartType} />
            <ButtonReload loading={isLoadingDynamics} onReload={loadCallDynamics} />
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          {callDynamics.length === 0 && !isLoadingDynamics && (
            <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
          )}
          {callDynamics.length > 0 && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Пик нагрузки: {maxCalls.toLocaleString()} звонков/{dynamicsUnitLabel}
              </div>
              {chartType === 'bar' ? (
                <div className="space-y-2">
                  <div className="relative h-64 w-full min-w-0 rounded-md bg-muted/30 px-2 py-4">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                      <BarChart data={lineChartData} margin={{ top: 12, right: 24, left: 0, bottom: 32 }}>
                        <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          width={60}
                          domain={[0, Math.ceil(1.1 * maxCalls)]}
                          tickFormatter={callsAxisFormatter}
                          tick={{ fontSize: 11 }}
                        />
                        <RechartsTooltip
                          wrapperStyle={tooltipWrapperStyle}
                          content={<ChartTooltip formatter={callDynamicsTooltipFormatter} />}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar
                          dataKey="incomingCalls"
                          name="Входящие"
                          stackId="calls"
                          fill="#0ea5e9"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="outgoingCalls"
                          name="Исходящие"
                          stackId="calls"
                          fill="#db2777"
                          radius={[4, 4, 0, 0]}
                        >
                          <LabelList
                            dataKey="calls"
                            content={createValueLabelRenderer('#0f172a', callsAxisFormatter)}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative h-64 w-full min-w-0 rounded-md bg-muted/30 px-2 py-4">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                      <ReLineChart data={lineChartData} margin={{ top: 12, right: 24, left: 0, bottom: 32 }}>
                        <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          width={60}
                          domain={[0, Math.ceil(1.1 * maxCalls)]}
                          tickFormatter={callsAxisFormatter}
                          tick={{ fontSize: 11 }}
                        />
                        <RechartsTooltip
                          wrapperStyle={tooltipWrapperStyle}
                          content={<ChartTooltip formatter={callDynamicsTooltipFormatter} />}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                          type="monotone"
                          dataKey="incomingCalls"
                          name="Входящие"
                          stroke="#0ea5e9"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        >
                          <LabelList
                            dataKey="incomingCalls"
                            content={createValueLabelRenderer('#0ea5e9', callsAxisFormatter)}
                          />
                        </Line>
                        <Line
                          type="monotone"
                          dataKey="outgoingCalls"
                          name="Исходящие"
                          stroke="#db2777"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        >
                          <LabelList
                            dataKey="outgoingCalls"
                            content={createValueLabelRenderer('#db2777', callsAxisFormatter)}
                          />
                        </Line>
                      </ReLineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-0 pb-2">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ArrowDown className="h-5 w-5" />
                Воронка
              </CardTitle>
              <CardDescription>Заказы → попытки допродажи → успешные допродажи</CardDescription>
            </div>
            <ButtonReload loading={isLoadingFunnel} onReload={loadFunnel} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingFunnel && <p className="text-sm text-muted-foreground">Загрузка...</p>}
          {!isLoadingFunnel && !upsellFunnel && (
            <p className="text-sm text-muted-foreground">Нет данных по воронке.</p>
          )}

          <div className="space-y-3">
            {funnelSteps.map((step, index) => (
              <div key={step.key} className="space-y-2">
                {index > 0 && (
                  <div className="text-center text-xs text-muted-foreground">
                    Конверсия: {step.ratio.toFixed(1)}%
                  </div>
                )}
                <div className="rounded-md border bg-muted/20 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{step.title}</span>
                    <span className="text-sm font-semibold">{step.value.toLocaleString('ru-RU')}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-gray-800">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${Math.max(0, Math.min(100, step.ratio))}%`,
                        backgroundColor: step.color
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ComplaintsNegativityChart dateRange={dateRange} callType={callType} />
        </div>
        <CallTopicsChart dateRange={dateRange} callType={callType} />
      </div>
    </div>
  );
};

const ButtonReload = ({ loading, onReload }) => (
  <button
    type="button"
    onClick={onReload}
    disabled={loading}
    className="inline-flex items-center rounded-md border px-3 py-1 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50"
  >
    <Filter className="mr-2 h-4 w-4" />
    {loading ? 'Обновление...' : 'Обновить'}
  </button>
);

const ChartToggleButton = ({ chartType, onToggle }) => {
  const isBar = chartType === 'bar';
  const Icon = isBar ? LineChart : BarChart3;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center rounded-md border px-3 py-1 text-sm text-muted-foreground hover:bg-muted"
    >
      <Icon className="mr-2 h-4 w-4" />
      {isBar ? 'Линия' : 'Столбцы'}
    </button>
  );
};

export default GeneralStatistics;
